import {
    useEffect,
    useState,
    useRef
} from "react";
import {
    Animated
} from "react-native";
import type {
    ChartValueTransitionConstantType,
    ChartValueTransitionLayer,
    ChartValueTransition
} from "./type";
import {
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    useReducedMotion,
    getMotionTiming,
    MOTION_EASINGS
} from "../../helpers/motion";

type ChartValueTransitionMode = "interpolate" | "crossfade";

type ChartValueTransitionPlan = {
    exitEasing: (value: number) => number;
    previous?: ChartValueTransitionLayer;
    easing: (value: number) => number;
    mode: ChartValueTransitionMode;
    from: Array<number>;
    to: Array<number>;
    duration: number;
    opacity: number;
};

const CHART_VALUE_TRANSITION_START = 0;

const CHART_VALUE_TRANSITION_END = 1;

const getChartValueTransitionKey = (values: Array<number>, shapeKey: string): string => {
    return `${shapeKey}|${values.length}|${values.join(",")}`;
};

const getChartValueTransitionFrame = (plan: ChartValueTransitionPlan, progress: number): ChartValueTransition => {
    const eased = plan.easing(progress);

    const exitProgress = plan.exitEasing(progress);

    const previous = plan.previous && progress < CHART_VALUE_TRANSITION_END ? {
        opacity: plan.previous.opacity * (CHART_VALUE_TRANSITION_END - exitProgress),
        values: plan.previous.values
    } : undefined;

    const opacity = plan.opacity + (CHART_VALUE_TRANSITION_END - plan.opacity) * eased;

    if(plan.mode === "crossfade") {
        return {
            values: plan.to,
            previous,
            opacity
        };
    }

    return {
        values: plan.to.map((value, index) => {
            const origin = plan.from[index] ?? value;

            return origin + (value - origin) * eased;
        }),
        previous,
        opacity
    };
};

const useChartValueTransition = ({
    customTheme,
    shapeKey,
    values
}: ChartValueTransitionConstantType): ChartValueTransition => {
    const {
        durations
    } = NCoreUIKitTheme.useContext(customTheme);

    const isReduceMotionEnabled = useReducedMotion();

    const key = getChartValueTransitionKey(values, shapeKey);

    const keyRef = useRef<string>(key);

    const shapeKeyRef = useRef<string>(shapeKey);

    const tokenRef = useRef<number>(0);

    const isMountedRef = useRef<boolean>(false);

    const progressRef = useRef<number>(CHART_VALUE_TRANSITION_END);

    const planRef = useRef<ChartValueTransitionPlan>({
        opacity: CHART_VALUE_TRANSITION_END,
        exitEasing: MOTION_EASINGS.linear,
        easing: MOTION_EASINGS.linear,
        mode: "interpolate",
        from: values,
        duration: 0,
        to: values
    });

    const [
        ,
        setFrame
    ] = useState<number>(0);

    if(keyRef.current !== key) {
        const current = getChartValueTransitionFrame(planRef.current, progressRef.current);

        const isSameShape = shapeKeyRef.current === shapeKey && current.values.length === values.length;

        const isInterpolated = isSameShape && !isReduceMotionEnabled;

        const timing = getMotionTiming({
            intent: isInterpolated ? "valueChange" : "surfaceEnter",
            motion: isInterpolated ? undefined : "fade",
            isReduceMotionEnabled,
            durations
        });

        const exitTiming = getMotionTiming({
            isReduceMotionEnabled,
            intent: "listRemove",
            motion: "fade",
            durations
        });

        planRef.current = isInterpolated ? {
            exitEasing: exitTiming.easing,
            previous: current.previous,
            duration: timing.duration,
            opacity: current.opacity,
            easing: timing.easing,
            from: current.values,
            mode: "interpolate",
            to: values
        } : {
            duration: Math.max(timing.duration, exitTiming.duration),
            opacity: CHART_VALUE_TRANSITION_START,
            previous: current.opacity > 0 ? {
                opacity: current.opacity,
                values: current.values
            } : current.previous,
            exitEasing: exitTiming.easing,
            easing: timing.easing,
            mode: "crossfade",
            from: values,
            to: values
        };

        keyRef.current = key;

        shapeKeyRef.current = shapeKey;

        tokenRef.current += 1;

        progressRef.current = planRef.current.duration > 0 ? CHART_VALUE_TRANSITION_START : CHART_VALUE_TRANSITION_END;
    }

    useEffect(() => {
        if(!isMountedRef.current) {
            isMountedRef.current = true;

            return;
        }

        const plan = planRef.current;

        const token = tokenRef.current;

        if(plan.duration <= 0 || progressRef.current >= CHART_VALUE_TRANSITION_END) {
            progressRef.current = CHART_VALUE_TRANSITION_END;

            setFrame((frame) => frame + 1);

            return;
        }

        const animatedValue = new Animated.Value(progressRef.current);

        const listenerID = animatedValue.addListener(({
            value
        }) => {
            if(tokenRef.current !== token) {
                return;
            }

            progressRef.current = value;

            setFrame((frame) => frame + 1);
        });

        const animation = Animated.timing(animatedValue, {
            duration: plan.duration * (CHART_VALUE_TRANSITION_END - progressRef.current),
            toValue: CHART_VALUE_TRANSITION_END,
            easing: MOTION_EASINGS.linear,
            useNativeDriver: false
        });

        animation.start(({
            finished
        }) => {
            if(!finished || tokenRef.current !== token) {
                return;
            }

            progressRef.current = CHART_VALUE_TRANSITION_END;

            setFrame((frame) => frame + 1);
        });

        return () => {
            animation.stop();

            animatedValue.removeListener(listenerID);
        };
    }, [
        key
    ]);

    return getChartValueTransitionFrame(planRef.current, progressRef.current);
};
export default useChartValueTransition;
